import { addDays, weekdayOf } from './localDate.js';
import { formatRRule, parseRRule } from './rrule.js';
import { WEEKDAYS } from './types.js';
/** Kalenderdatum (YYYY-MM-DD) → RFC-5545-weekdagcode. */
export function weekdayCode(key) {
    return weekdayOf(key);
}
/** De hoeveelste weekdag van de maand (1..4) of -1 als het de laatste is (bv. laatste vrijdag). */
export function nthOfMonth(key) {
    const day = Number(key.slice(8, 10));
    const inNextWeek = addDays(key, 7);
    if (inNextWeek.slice(5, 7) !== key.slice(5, 7))
        return -1;
    return Math.min(4, Math.ceil(day / 7));
}
export function defaultRepeat(date) {
    return {
        kind: 'none',
        weekdays: [weekdayCode(date)],
        monthly: 'day',
        nth: nthOfMonth(date),
        parity: 'all',
        ends: 'never',
        count: 10,
        until: addDays(date, 90),
    };
}
function sortWeekdays(days) {
    const seen = [];
    for (const d of days)
        if (seen.indexOf(d) < 0)
            seen.push(d);
    return seen.sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b));
}
/** Formulier → regel (null bij "niet herhalen"). */
export function repeatToRule(form, date) {
    switch (form.kind) {
        case 'none':
            return null;
        case 'daily':
            return { freq: 'DAILY' };
        case 'weekly':
        case 'biweekly': {
            const days = form.weekdays.length > 0 ? sortWeekdays(form.weekdays) : [weekdayCode(date)];
            return {
                freq: 'WEEKLY',
                ...(form.kind === 'biweekly' ? { interval: 2 } : {}),
                byDay: days.map((weekday) => ({ weekday })),
            };
        }
        case 'monthly':
            if (form.monthly === 'last')
                return { freq: 'MONTHLY', byMonthDay: [-1] };
            if (form.monthly === 'nth') {
                const byDay = [{ weekday: weekdayCode(date), ordinal: form.nth }];
                return { freq: 'MONTHLY', byDay };
            }
            return { freq: 'MONTHLY', byMonthDay: [Number(date.slice(8, 10))] };
        case 'yearly':
            return { freq: 'YEARLY' };
    }
}
/** Formulier → API-invoer `{rrule, count?, until?, week_parity?}`. */
export function repeatToInput(form, date) {
    const rule = repeatToRule(form, date);
    if (!rule)
        return null;
    const out = { rrule: formatRRule(rule) };
    if (form.ends === 'count')
        out.count = Math.max(1, Math.min(1000, Math.round(form.count)));
    if (form.ends === 'until' && form.until)
        out.until = form.until;
    if ((form.kind === 'weekly' || form.kind === 'biweekly') && form.parity !== 'all') {
        out.week_parity = form.parity;
    }
    return out;
}
/** API-herhaling (EventDetail.recurrence) → formulier; onbekende vormen vallen terug op "wekelijks". */
export function repeatFromInput(rec, date) {
    const base = defaultRepeat(date);
    if (!rec)
        return base;
    let parsed;
    try {
        parsed = parseRRule(rec.rrule);
    }
    catch {
        return { ...base, kind: 'weekly' };
    }
    const rule = parsed.rule;
    const count = rec.count ?? parsed.count ?? null;
    const until = rec.until ?? parsed.until?.slice(0, 10) ?? null;
    const form = {
        ...base,
        ends: count ? 'count' : until ? 'until' : 'never',
        count: count ?? base.count,
        until: until ? `${until.slice(0, 4)}-${until.slice(4, 6)}-${until.slice(6, 8)}` : base.until,
        parity: rec.week_parity ?? 'all',
    };
    // parseRRule levert UNTIL als YYYYMMDD; de API geeft `until` al als YYYY-MM-DD.
    if (rec.until)
        form.until = rec.until;
    switch (rule.freq) {
        case 'DAILY':
            form.kind = 'daily';
            break;
        case 'WEEKLY':
            form.kind = (rule.interval ?? 1) === 2 ? 'biweekly' : 'weekly';
            form.weekdays = rule.byDay?.length
                ? sortWeekdays(rule.byDay.map((d) => d.weekday))
                : [weekdayCode(date)];
            break;
        case 'MONTHLY':
            form.kind = 'monthly';
            if (rule.byMonthDay && rule.byMonthDay.indexOf(-1) >= 0)
                form.monthly = 'last';
            else if (rule.byDay?.length) {
                form.monthly = 'nth';
                form.nth = rule.byDay[0]?.ordinal ?? nthOfMonth(date);
            }
            else
                form.monthly = 'day';
            break;
        case 'YEARLY':
            form.kind = 'yearly';
            break;
    }
    return form;
}
/** Zelfde herhaling? (dan mag "alleen deze keer" nog; anders alleen volgende/hele reeks). */
export function sameRepeat(a, b) {
    if (!a || !b)
        return a === b;
    return (a.rrule === b.rrule &&
        (a.count ?? null) === (b.count ?? null) &&
        (a.until ?? null) === (b.until ?? null) &&
        (a.week_parity ?? null) === (b.week_parity ?? null));
}
//# sourceMappingURL=form.js.map